import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, ExternalLink, ArrowRight, Play, Mail, Maximize2, X, Volume2, Menu, Instagram, Twitter, Youtube, Linkedin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Font package CSS
const FONT_CSS = {
  editorial: `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Source+Sans+Pro:wght@300;400;600&display=swap');
  `,
  modern: `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  `,
  bold: `
    @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Roboto:wght@300;400;500&display=swap');
  `,
  elegant: `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');
  `,
  minimal: ``
};

const FONT_FAMILIES = {
  editorial: { heading: "'Playfair Display', Georgia, serif", body: "'Source Sans Pro', sans-serif" },
  modern: { heading: "'Inter', sans-serif", body: "'Inter', sans-serif" },
  bold: { heading: "'Oswald', sans-serif", body: "'Roboto', sans-serif" },
  elegant: { heading: "'Cormorant Garamond', serif", body: "'Lato', sans-serif" },
  minimal: { heading: "-apple-system, BlinkMacSystemFont, sans-serif", body: "-apple-system, sans-serif" }
};

// Convert Google Drive URLs to embeddable format
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  
  // Already converted formats
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) {
    return url;
  }
  
  // Handle /file/d/ format: https://drive.google.com/file/d/FILE_ID/view
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1200`;
  }
  
  // Handle /open?id= format: https://drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1200`;
  }
  
  // Handle /uc?id= format: https://drive.google.com/uc?id=FILE_ID
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:.*&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) {
    return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1200`;
  }
  
  // Handle sharing URLs: https://drive.google.com/drive/folders/... or just the ID
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) {
    return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w1200`;
  }
  
  return url;
};

// Get YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

// Check if URL is a direct video file (mp4, webm, etc.)
const isDirectVideoUrl = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

// ==================== SECTION COMPONENTS ====================

const HeroSection = ({ section, site }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const videoRef = useRef(null);
  
  // Detect mobile viewport and iOS
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowFullscreen(false);
    };
    if (showFullscreen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [showFullscreen]);
  
  // Use mobile video if available and on mobile, otherwise use desktop video
  const activeVideoUrl = (isMobile && section.video_url_mobile) ? section.video_url_mobile : section.video_url;
  const videoId = getYouTubeId(activeVideoUrl);
  const isDirectVideo = isDirectVideoUrl(activeVideoUrl);
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  // YouTube thumbnail URL for fallback (only for YouTube videos)
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  
  // Handle fullscreen for direct video
  const handleFullscreenClick = () => {
    if (isDirectVideo && videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.muted = false;
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitEnterFullscreen) {
        videoRef.current.muted = false;
        videoRef.current.webkitEnterFullscreen();
      }
    } else {
      setShowFullscreen(true);
    }
  };
  
  return (
    <>
      {/* Fullscreen Video Modal - YouTube only */}
      {showFullscreen && videoId && !isDirectVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setShowFullscreen(false)}
        >
          {/* Close button */}
          <button 
            className="absolute top-6 right-6 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setShowFullscreen(false)}
          >
            <X size={24} className="text-white" />
          </button>
          
          {/* Fullscreen video with sound */}
          <div 
            className="w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1&playsinline=1`}
              title="Video Player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
      
      <section 
        className={`relative min-h-screen overflow-hidden ${
          section.content_position && section.content_position !== 'center' 
            ? '' 
            : 'flex items-center justify-center'
        }`}
        style={{ backgroundColor: section.background_value || '#0a0a0a' }}
      >
        {/* Direct MP4/Video Background */}
        {isDirectVideo ? (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* 16:9 Video Container */}
            <div 
              className="relative z-1"
              style={{ 
                width: isMobile ? '100%' : 'min(177.78vh, 100%)',
                aspectRatio: '16/9',
                transform: 'scale(1.15)',
                transformOrigin: 'center center'
              }}
            >
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={activeVideoUrl}
                autoPlay
                muted
                loop
                playsInline
                webkit-playsinline="true"
              />
            </div>
            
            {/* Overlay */}
            <div className="absolute inset-0 z-10 pointer-events-none" style={{
              backgroundColor: section.overlay_color === 'white' 
                ? `rgba(255,255,255,${section.overlay_opacity || 0.3})`
                : `rgba(0,0,0,${section.overlay_opacity || 0.3})`
            }} />
            
            {/* Fullscreen button */}
            <button
              onClick={handleFullscreenClick}
              className="absolute bottom-6 right-6 z-20 flex items-center gap-2 px-4 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition-all group"
              title="Watch fullscreen with sound"
            >
              <Volume2 size={18} className="text-white/80 group-hover:text-white" />
              <Maximize2 size={18} className="text-white/80 group-hover:text-white" />
              <span className="text-white/80 group-hover:text-white text-sm font-medium hidden sm:inline">
                Watch with Sound
              </span>
            </button>
          </div>
        ) : videoId ? (
          /* YouTube Video Background */
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Fallback thumbnail image for iOS and slow connections */}
            {thumbnailUrl && (
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `url(${thumbnailUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
            )}
            
            {/* 16:9 Video Container - centered */}
            <div 
              className="relative z-1"
              style={{ 
                width: isMobile ? '100%' : 'min(177.78vh, 100%)',
                aspectRatio: '16/9',
                transform: 'scale(1.15)',
                transformOrigin: 'center center'
              }}
            >
              <iframe
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ border: 'none' }}
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&iv_load_policy=3&disablekb=1&fs=0`}
                title="Background Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                playsInline
                webkit-playsinline="true"
              />
            </div>
            
            {/* YouTube Title Cover - hides the title bar at top */}
            {section.hide_video_title && (
              <div 
                className="absolute top-0 left-0 right-0 z-5 pointer-events-none"
                style={{
                  height: '120px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)'
                }}
              />
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 z-10 pointer-events-none" style={{
              backgroundColor: section.overlay_color === 'white' 
                ? `rgba(255,255,255,${section.overlay_opacity || 0.3})`
                : `rgba(0,0,0,${section.overlay_opacity || 0.3})`
            }} />
            
            {/* Fullscreen button - bottom right */}
            <button
              onClick={handleFullscreenClick}
              className="absolute bottom-6 right-6 z-20 flex items-center gap-2 px-4 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition-all group"
              title="Watch fullscreen with sound"
            >
              <Volume2 size={18} className="text-white/80 group-hover:text-white" />
              <Maximize2 size={18} className="text-white/80 group-hover:text-white" />
              <span className="text-white/80 group-hover:text-white text-sm font-medium hidden sm:inline">
                Watch with Sound
              </span>
            </button>
          </div>
        ) : section.background_type === 'image' && section.background_value ? (
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${convertGoogleDriveUrl(section.background_value)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0" style={{
            backgroundColor: section.overlay_color === 'white' 
              ? `rgba(255,255,255,${section.overlay_opacity || 0.5})`
              : `rgba(0,0,0,${section.overlay_opacity || 0.5})`
          }} />
        </div>
      ) : (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: section.background_value || '#0a0a0a' }}
        />
      )}
      
      {/* Content */}
      <div 
        className={`relative z-20 px-6 py-20 max-w-4xl ${
          section.content_position === 'top-center' 
            ? 'mx-auto text-center pt-24' 
            : section.content_position === 'bottom-center'
            ? 'mx-auto text-center mt-auto pb-24'
            : section.content_position === 'bottom-right'
            ? 'ml-auto mr-8 text-right mt-auto pb-24'
            : 'mx-auto text-center'  /* default center */
        }`}
        style={{
          color: site.dark_mode ? '#ffffff' : '#0a0a0a',
          position: section.content_position && section.content_position !== 'center' ? 'absolute' : 'relative',
          ...(section.content_position === 'top-center' && { top: 0, left: 0, right: 0 }),
          ...(section.content_position === 'bottom-center' && { bottom: 0, left: 0, right: 0 }),
          ...(section.content_position === 'bottom-right' && { bottom: 0, right: 0 }),
        }}
      >
        {site.logo && !section.hero_logo && (
          <img 
            src={convertGoogleDriveUrl(site.logo)} 
            alt={site.name}
            className={`h-16 md:h-24 mb-8 ${section.content_position === 'bottom-right' ? 'ml-auto' : 'mx-auto'}`}
          />
        )}
        
        {/* Hero Logo (replaces title) or Title */}
        {section.hero_logo ? (
          <img 
            src={convertGoogleDriveUrl(section.hero_logo)} 
            alt={site.name}
            className={`max-h-32 md:max-h-48 lg:max-h-64 mb-6 object-contain ${section.content_position === 'bottom-right' ? 'ml-auto' : 'mx-auto'}`}
          />
        ) : (
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-6"
            style={{ fontFamily: fonts.heading }}
          >
            {site.name}
          </h1>
        )}
        
        {site.tagline && (
          <p className="text-xl md:text-2xl opacity-80 mb-10" style={{ fontFamily: fonts.body }}>
            {site.tagline}
          </p>
        )}
        
        {/* Navigation Pills */}
        {site.nav_links?.length > 0 && (
          <nav className="flex flex-wrap items-center justify-center gap-4">
            {site.nav_links.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                className="px-6 py-2 border rounded-full text-sm uppercase tracking-widest transition-all duration-300"
                style={{ 
                  borderColor: site.dark_mode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                }}
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}

        {section.button_text && section.button_url && (
          <a
            href={section.button_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 px-8 py-4 text-lg font-bold uppercase tracking-widest transition-all"
            style={{ 
              backgroundColor: site.accent_color,
              color: '#ffffff'
            }}
          >
            {section.button_text}
            <ExternalLink size={18} />
          </a>
        )}
      </div>
    </section>
    </>
  );
};

const AboutSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  const linkedProjects = section.linked_projects || [];
  const linkedCompanies = section.linked_companies || [];
  
  return (
    <section 
      id="about"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#111111' : '#f5f5f5') }}
    >
      <div className="max-w-5xl mx-auto">
        <div className={`grid ${section.image ? 'md:grid-cols-2 gap-12 items-start' : ''}`}>
          {section.image && (
            <div className="overflow-hidden rounded-lg">
              <img
                src={convertGoogleDriveUrl(section.image)}
                alt={section.title}
                className="w-full h-auto object-contain"
              />
            </div>
          )}
          
          <div>
            <h2 
              className="text-4xl md:text-5xl font-light mb-8"
              style={{ 
                fontFamily: fonts.heading,
                color: site.dark_mode ? '#ffffff' : '#0a0a0a'
              }}
            >
              {section.title}
            </h2>
            {section.content && (
              <div 
                className="prose prose-lg"
                style={{ 
                  fontFamily: fonts.body,
                  color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
                }}
              >
                {section.content.split('\n\n').map((p, i) => (
                  <p key={i} className="mb-4 leading-relaxed">{p}</p>
                ))}
              </div>
            )}
            
            {/* Linked Projects */}
            {linkedProjects.length > 0 && (
              <div className="mt-8">
                <h4 className="text-sm uppercase tracking-widest mb-3" style={{ color: site.accent_color }}>
                  Projects
                </h4>
                <div className="flex flex-wrap gap-2">
                  {linkedProjects.map((project, idx) => (
                    <a
                      key={project.project_id || idx}
                      href={project.external_url || `/${project.slug}`}
                      className="px-3 py-1 text-sm rounded transition-colors"
                      style={{ 
                        backgroundColor: site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: site.dark_mode ? '#ffffff' : '#0a0a0a'
                      }}
                    >
                      {project.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* Linked Companies */}
            {linkedCompanies.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm uppercase tracking-widest mb-3" style={{ color: site.accent_color }}>
                  Companies
                </h4>
                <div className="flex flex-wrap gap-2">
                  {linkedCompanies.map((company, idx) => (
                    <a
                      key={company.company_id || idx}
                      href={`/${company.slug}`}
                      className="px-3 py-1 text-sm rounded transition-colors"
                      style={{ 
                        backgroundColor: site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: site.dark_mode ? '#ffffff' : '#0a0a0a'
                      }}
                    >
                      {company.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* Social Links */}
            {site.social_links && Object.keys(site.social_links).length > 0 && (
              <div className="flex gap-4 mt-8">
                {Object.entries(site.social_links).map(([platform, url]) => url && (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border rounded text-sm uppercase tracking-widest hover:opacity-80 transition"
                    style={{ 
                      borderColor: site.dark_mode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      color: site.dark_mode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
                    }}
                  >
                    {platform}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const ProjectsSection = ({ section, site, content }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Combine custom items with linked projects from CR
  const customItems = section.items || [];
  const linkedProjects = section.linked_projects || [];
  
  // Convert linked projects to display format
  const linkedProjectItems = linkedProjects.map(p => ({
    id: p.project_id,
    title: p.title,
    description: p.description || p.tagline || p.synopsis,
    dek: p.tagline,
    image: p.poster_image || p.hero_image || p.image,
    video_url: p.trailer_url || p.video_url,
    external_url: p.external_url,
    slug: p.slug,
    isCRProject: true
  }));
  
  // Don't show fallback news - only show actual projects
  const projects = [...linkedProjectItems, ...customItems];
  
  // Get linked artists to display
  const linkedArtists = section.linked_artists || [];
  
  if (projects.length === 0 && linkedArtists.length === 0) return null;
  
  return (
    <section 
      id="projects"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#0a0a0a' : '#ffffff') }}
    >
      <div className="max-w-6xl mx-auto">
        <h2 
          className="text-4xl md:text-5xl font-light mb-8 text-center"
          style={{ 
            fontFamily: fonts.heading,
            color: site.dark_mode ? '#ffffff' : '#0a0a0a'
          }}
        >
          {section.title}
        </h2>
        
        {/* Linked Artists */}
        {linkedArtists.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {linkedArtists.map((artist, idx) => (
              <a
                key={artist.artist_id || idx}
                href={`/artists`}
                className="flex items-center gap-3 px-4 py-2 rounded-full transition-all hover:scale-105"
                style={{ 
                  backgroundColor: site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: site.dark_mode ? '#ffffff' : '#0a0a0a'
                }}
              >
                {artist.profile_image && (
                  <img 
                    src={convertGoogleDriveUrl(artist.profile_image)} 
                    alt={artist.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <span className="font-medium">{artist.name}</span>
              </a>
            ))}
          </div>
        )}
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.slice(0, 9).map((project, idx) => (
            <div
              key={project.id || idx}
              className="group"
            >
              {/* Image/Video Container */}
              <div 
                className="relative aspect-[4/3] overflow-hidden mb-4 cursor-pointer"
                style={{ backgroundColor: site.dark_mode ? '#1a1a1a' : '#f0f0f0' }}
                onClick={() => project.video_url && setPlayingVideo(playingVideo === project.id ? null : project.id)}
              >
                {playingVideo === project.id && project.video_url ? (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${getYouTubeId(project.video_url)}?autoplay=1&rel=0`}
                    title={project.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    {project.image && (
                      <img
                        src={convertGoogleDriveUrl(project.image)}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    {project.video_url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                          <Play size={28} className="text-black ml-1" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Title - Clickable */}
              <a
                href={project.external_url || project.link || (project.slug ? `/${project.slug}` : '#')}
                target={project.external_url ? "_blank" : undefined}
                rel={project.external_url ? "noopener noreferrer" : undefined}
              >
                <h3 
                  className="text-xl font-medium hover:opacity-80 transition-opacity"
                  style={{ color: site.dark_mode ? '#ffffff' : '#0a0a0a' }}
                >
                  {project.title}
                </h3>
              </a>
              
              {/* Description */}
              {(project.description || project.dek) && (
                <p 
                  className="text-sm mt-2 line-clamp-3"
                  style={{ color: site.dark_mode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}
                >
                  {project.description || project.dek}
                </p>
              )}
              
              {/* Video indicator */}
              {project.video_url && (
                <p 
                  className="text-xs mt-2 flex items-center gap-1"
                  style={{ color: site.accent_color }}
                >
                  <Play size={12} /> Click to play trailer
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PressSection = ({ section, site, content }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  // Get linked entity names from THIS section's filters (not site-wide)
  const linkedArtistNames = (section.linked_artists || []).map(a => a.name?.toLowerCase());
  const linkedProjectNames = (section.linked_projects || []).map(p => p.title?.toLowerCase());
  const linkedCompanyNames = (section.linked_companies || []).map(c => c.name?.toLowerCase());
  
  const hasFilters = linkedArtistNames.length > 0 || linkedProjectNames.length > 0 || linkedCompanyNames.length > 0;
  
  // Filter press content based on selected entities
  const press = content.filter(c => {
    // If no filters, show all press items
    if (!hasFilters) {
      return c.type === 'Press' || c.category === 'Press';
    }
    
    // Check against artist filters
    if (linkedArtistNames.length > 0 && c.artist) {
      if (linkedArtistNames.some(name => name && c.artist.toLowerCase().includes(name))) {
        return true;
      }
    }
    
    // Check against project filters
    if (linkedProjectNames.length > 0 && c.project) {
      if (linkedProjectNames.some(name => name && c.project.toLowerCase().includes(name))) {
        return true;
      }
    }
    
    // Check against company filters
    if (linkedCompanyNames.length > 0 && c.company) {
      if (linkedCompanyNames.some(name => name && c.company.toLowerCase().includes(name))) {
        return true;
      }
    }
    
    return false;
  });
  
  // Also include custom press items submitted from the brand site
  const customPress = (section.press_items || [])
    .filter(item => item.status === 'approved' || !item.status) // Show approved or items without status
    .map(item => ({
      content_id: item.id,
      title: item.title,
      source: item.source,
      slug: item.url, // External URL
      external_url: item.url,
      date: item.date,
      isCustom: true
    }));
  
  const allPress = [...press, ...customPress];
  
  if (allPress.length === 0) return null;
  
  return (
    <section 
      id="press"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#111111' : '#f5f5f5') }}
    >
      <div className="max-w-5xl mx-auto">
        <h2 
          className="text-4xl md:text-5xl font-light mb-12 text-center"
          style={{ 
            fontFamily: fonts.heading,
            color: site.dark_mode ? '#ffffff' : '#0a0a0a'
          }}
        >
          {section.title}
        </h2>
        
        <div className="space-y-4">
          {allPress.slice(0, 10).map((item, idx) => (
            item.isCustom || item.external_url ? (
              <a
                key={item.content_id || idx}
                href={item.external_url || item.slug}
                target="_blank"
                rel="noopener noreferrer"
                className="block group p-6 transition-all hover:translate-x-2"
                style={{ 
                  backgroundColor: site.dark_mode ? '#0a0a0a' : '#ffffff',
                  borderLeft: `3px solid transparent`
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = site.accent_color}
                onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span 
                    className="text-xs uppercase tracking-widest"
                    style={{ color: site.accent_color }}
                  >
                    {item.source || item.category || item.type}
                  </span>
                  {item.date && (
                    <span className="text-xs text-muted-foreground">
                      • {new Date(item.date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <h3 
                  className="text-lg md:text-xl font-medium"
                  style={{ color: site.dark_mode ? '#ffffff' : '#0a0a0a' }}
                >
                  {item.title}
                </h3>
              </a>
            ) : (
              <Link
                key={item.content_id || idx}
                to={`/${item.slug}`}
                className="block group p-6 transition-all hover:translate-x-2"
                style={{ 
                  backgroundColor: site.dark_mode ? '#0a0a0a' : '#ffffff',
                  borderLeft: `3px solid transparent`
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = site.accent_color}
                onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span 
                    className="text-xs uppercase tracking-widest"
                    style={{ color: site.accent_color }}
                  >
                    {item.source || item.category || item.type}
                  </span>
                  {item.artist && (
                    <span className="text-xs text-muted-foreground">
                      • {item.artist}
                    </span>
                  )}
                </div>
                <h3 
                  className="text-lg md:text-xl font-medium"
                  style={{ color: site.dark_mode ? '#ffffff' : '#0a0a0a' }}
                >
                  {item.title}
                </h3>
                {item.dek && (
                  <p 
                    className="text-sm mt-1 line-clamp-2"
                    style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                  >
                    {item.dek}
                  </p>
                )}
              </Link>
            )
          ))}
        </div>
      </div>
    </section>
  );
};

const VideoSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  const videoId = getYouTubeId(section.video_url);
  
  if (!videoId && !section.embed_code) return null;
  
  return (
    <section 
      id="video"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#0a0a0a' : '#ffffff') }}
    >
      <div className="max-w-4xl mx-auto">
        <h2 
          className="text-4xl md:text-5xl font-light mb-12 text-center"
          style={{ 
            fontFamily: fonts.heading,
            color: site.dark_mode ? '#ffffff' : '#0a0a0a'
          }}
        >
          {section.title}
        </h2>
        
        <div className="aspect-video">
          {videoId ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}`}
              title={section.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : section.embed_code ? (
            <div dangerouslySetInnerHTML={{ __html: section.embed_code }} />
          ) : null}
        </div>
      </div>
    </section>
  );
};

const BookSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  return (
    <section 
      id="book"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#111111' : '#f5f5f5') }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {section.image && (
            <div>
              <img
                src={convertGoogleDriveUrl(section.image)}
                alt={section.title}
                className="w-full max-w-md mx-auto"
              />
            </div>
          )}
          
          <div>
            {section.subtitle && (
              <div 
                className="text-sm uppercase tracking-widest mb-2"
                style={{ color: site.accent_color }}
              >
                {section.subtitle}
              </div>
            )}
            <h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ 
                fontFamily: fonts.heading,
                color: site.dark_mode ? '#ffffff' : '#0a0a0a'
              }}
            >
              {section.title}
            </h2>
            {section.content && (
              <p 
                className="text-lg mb-8"
                style={{ 
                  fontFamily: fonts.body,
                  color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
                }}
              >
                {section.content}
              </p>
            )}
            
            {section.button_text && section.button_url && (
              <a
                href={section.button_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 text-lg font-bold uppercase tracking-widest transition-all"
                style={{ 
                  backgroundColor: site.accent_color,
                  color: '#ffffff'
                }}
              >
                {section.button_text}
                <ExternalLink size={18} />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Full-Width Image Section
const FullWidthImageSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  const imageUrl = section.fullwidth_image || section.image;
  
  if (!imageUrl) return null;
  
  return (
    <section 
      id="fullwidth_image"
      className="w-full"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#0a0a0a' : '#ffffff') }}
    >
      <div className="w-full">
        <img
          src={convertGoogleDriveUrl(imageUrl)}
          alt={section.title || 'Full width image'}
          className="w-full h-auto object-cover"
          style={{ maxHeight: '90vh' }}
        />
        {section.caption && (
          <p 
            className="text-center py-4 text-sm"
            style={{ 
              fontFamily: fonts.body,
              color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
            }}
          >
            {section.caption}
          </p>
        )}
      </div>
    </section>
  );
};

// Swipeable Gallery Section
const GallerySection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site?.font_package] || FONT_FAMILIES.editorial;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Safety check - return null if section or site is invalid
  if (!section || !site) return null;
  
  // Use gallery_images if available, fallback to items - ensure it's an array
  const rawImages = section.gallery_images || section.items || [];
  const images = Array.isArray(rawImages) ? rawImages : [];
  
  // If no images and no single image, don't render
  if (images.length === 0 && !section.image) return null;
  
  // If only one image or using old format, show grid
  // Filter out any null/undefined items
  const displayImages = images.length > 0 
    ? images.filter(img => img && (img.url || typeof img === 'string'))
    : (section.image ? [{ url: section.image }] : []);
  
  // If after filtering we have no valid images, don't render
  if (displayImages.length === 0) return null;
  
  const isCarousel = displayImages.length > 1;
  
  // Helper to get YouTube video ID
  const getVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
      const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  };
  
  // Swipe handling
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < displayImages.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };
  
  const nextSlide = () => {
    setCurrentIndex(prev => prev < displayImages.length - 1 ? prev + 1 : 0);
  };
  
  const prevSlide = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : displayImages.length - 1);
  };
  
  return (
    <section 
      id="gallery"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#0a0a0a' : '#ffffff') }}
    >
      <div className="max-w-6xl mx-auto">
        {section.title && (
          <h2 
            className="text-4xl md:text-5xl font-light mb-12 text-center"
            style={{ 
              fontFamily: fonts.heading,
              color: site.dark_mode ? '#ffffff' : '#0a0a0a'
            }}
          >
            {section.title}
          </h2>
        )}
        
        {isCarousel ? (
          /* Swipeable Carousel */
          <div className="relative">
            {/* Main Image Container */}
            <div 
              className="relative overflow-hidden rounded-lg"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {displayImages.map((item, idx) => {
                  // Safely check if item is a video and has valid URL
                  const isVideo = item && item.type === 'video';
                  const videoId = isVideo && item.url ? getVideoId(item.url) : null;
                  const imageUrl = !isVideo && item ? (item.url || (typeof item === 'string' ? item : '')) : '';
                  
                  // Skip items without valid content
                  if (isVideo && !videoId && !imageUrl) return null;
                  
                  return (
                    <div 
                      key={item?.id || idx} 
                      className="w-full flex-shrink-0"
                    >
                      <div className="aspect-[16/9] overflow-hidden relative">
                        {isVideo && videoId ? (
                          playingVideo === idx ? (
                            /* Playing YouTube Video */
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&disablekb=1&fs=0`}
                              className="absolute inset-0 w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title={item?.caption || `Video ${idx + 1}`}
                            />
                          ) : (
                            /* Video Thumbnail with Play Button */
                            <div 
                              className="relative w-full h-full cursor-pointer group"
                              onClick={() => setPlayingVideo(idx)}
                            >
                              <img
                                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                alt={item?.caption || `Video ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                }}
                              />
                              {/* Play Button Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                                <div 
                                  className="w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                                  style={{ backgroundColor: site.accent_color || '#ff0000' }}
                                >
                                  <Play size={36} className="text-white ml-1" fill="white" />
                                </div>
                              </div>
                              {/* Video indicator */}
                              <div className="absolute top-4 left-4 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
                                VIDEO
                              </div>
                            </div>
                          )
                        ) : imageUrl ? (
                          /* Regular Image */
                          <img
                            src={convertGoogleDriveUrl(imageUrl)}
                            alt={item?.caption || `Gallery ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                      </div>
                      {item?.caption && (
                        <p 
                          className="text-center py-4 text-sm"
                          style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                        >
                          {item.caption}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all hover:scale-110"
              style={{ 
                backgroundColor: site.dark_mode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                color: site.dark_mode ? '#ffffff' : '#000000'
              }}
            >
              <ArrowRight size={24} className="rotate-180" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all hover:scale-110"
              style={{ 
                backgroundColor: site.dark_mode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                color: site.dark_mode ? '#ffffff' : '#000000'
              }}
            >
              <ArrowRight size={24} />
            </button>
            
            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {displayImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'w-8' : ''
                  }`}
                  style={{ 
                    backgroundColor: idx === currentIndex 
                      ? site.accent_color 
                      : (site.dark_mode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)')
                  }}
                />
              ))}
            </div>
            
            {/* Image Counter */}
            <p 
              className="text-center mt-4 text-sm"
              style={{ color: site.dark_mode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
            >
              {currentIndex + 1} / {displayImages.length}
            </p>
          </div>
        ) : (
          /* Single Image or Grid Fallback */
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {displayImages.map((item, idx) => {
              // Safely check if item is a video and has valid URL
              const isVideo = item && item.type === 'video';
              const videoId = isVideo && item.url ? getVideoId(item.url) : null;
              const imageUrl = !isVideo && item ? (item.url || (typeof item === 'string' ? item : '')) : '';
              
              // Skip items without valid content
              if (!videoId && !imageUrl) return null;
              
              return (
                <div key={idx} className="aspect-[16/9] overflow-hidden rounded-lg relative">
                  {isVideo && videoId ? (
                    playingVideo === idx ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&disablekb=1&fs=0`}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={item?.caption || `Video ${idx + 1}`}
                      />
                    ) : (
                      <div 
                        className="relative w-full h-full cursor-pointer group"
                        onClick={() => setPlayingVideo(idx)}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                          alt={item?.caption || `Video ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: site.accent_color || '#ff0000' }}
                          >
                            <Play size={20} className="text-white ml-0.5" fill="white" />
                          </div>
                        </div>
                      </div>
                    )
                  ) : imageUrl ? (
                    <img
                      src={convertGoogleDriveUrl(imageUrl)}
                      alt={item?.caption || `Gallery ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const ContactSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  return (
    <section 
      id="contact"
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#111111' : '#f5f5f5') }}
    >
      <div className="max-w-2xl mx-auto text-center">
        <h2 
          className="text-4xl md:text-5xl font-light mb-8"
          style={{ 
            fontFamily: fonts.heading,
            color: site.dark_mode ? '#ffffff' : '#0a0a0a'
          }}
        >
          {section.title}
        </h2>
        {section.content && (
          <p 
            className="text-lg mb-8"
            style={{ 
              fontFamily: fonts.body,
              color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
            }}
          >
            {section.content}
          </p>
        )}
        
        {section.button_text && section.button_url && (
          <a
            href={section.button_url}
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium transition-all"
            style={{ 
              backgroundColor: site.accent_color,
              color: '#ffffff'
            }}
          >
            <Mail size={18} />
            {section.button_text}
          </a>
        )}
      </div>
    </section>
  );
};

const CustomSection = ({ section, site }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  return (
    <section 
      id={section.title?.toLowerCase().replace(/\s+/g, '-')}
      className="py-24 px-6"
      style={{ backgroundColor: section.background_value || (site.dark_mode ? '#0a0a0a' : '#ffffff') }}
    >
      <div className="max-w-4xl mx-auto">
        <h2 
          className="text-4xl md:text-5xl font-light mb-8 text-center"
          style={{ 
            fontFamily: fonts.heading,
            color: site.dark_mode ? '#ffffff' : '#0a0a0a'
          }}
        >
          {section.title}
        </h2>
        {section.subtitle && (
          <p 
            className="text-xl text-center mb-8"
            style={{ color: site.accent_color }}
          >
            {section.subtitle}
          </p>
        )}
        {section.content && (
          <div 
            className="prose prose-lg mx-auto"
            style={{ 
              fontFamily: fonts.body,
              color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
            }}
          >
            {section.content.split('\n\n').map((p, i) => (
              <p key={i} className="mb-4 leading-relaxed">{p}</p>
            ))}
          </div>
        )}
        {section.image && (
          <div className="mt-8">
            <img
              src={convertGoogleDriveUrl(section.image)}
              alt={section.title}
              className="w-full max-w-2xl mx-auto"
            />
          </div>
        )}
        {section.button_text && section.button_url && (
          <div className="text-center mt-8">
            <a
              href={section.button_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-bold uppercase tracking-widest"
              style={{ 
                backgroundColor: site.accent_color,
                color: '#ffffff'
              }}
            >
              {section.button_text}
              <ArrowRight size={18} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// CR Ecosystem Banner
const CREcosystemBanner = ({ site }) => {
  return (
    <section 
      className="py-16 px-6 text-center"
      style={{ 
        backgroundColor: site.dark_mode ? '#0a0a0a' : '#ffffff',
        borderTop: `1px solid ${site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
      }}
    >
      <p 
        className="text-sm uppercase tracking-widest mb-4"
        style={{ color: site.dark_mode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
      >
        Part of the
      </p>
      <Link 
        to="/"
        className="inline-block text-2xl md:text-3xl font-light hover:opacity-80 transition-opacity"
        style={{ color: site.dark_mode ? '#ffffff' : '#0a0a0a' }}
      >
        Cultural Revolution
      </Link>
      <p 
        className="mt-4 text-sm"
        style={{ color: site.dark_mode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
      >
        Creation. Momentum. Ownership.
      </p>
    </section>
  );
};

// Site Header with navigation
const SiteHeader = ({ site, sections }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  
  // Get enabled sections for navigation
  const navSections = sections.filter(s => s.enabled && ['about', 'projects', 'press', 'contact', 'gallery', 'book'].includes(s.type));
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToSection = (sectionType) => {
    const element = document.getElementById(sectionType);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-3' : 'py-5'
      }`}
      style={{ 
        backgroundColor: scrolled 
          ? (site.dark_mode ? 'rgba(10,10,10,0.95)' : 'rgba(255,255,255,0.95)')
          : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo / Site Name */}
        <a 
          href="#" 
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-3"
        >
          {site.logo ? (
            <img 
              src={convertGoogleDriveUrl(site.logo)} 
              alt={site.name}
              className={`transition-all duration-300 ${scrolled ? 'h-8' : 'h-10'}`}
            />
          ) : (
            <span 
              className={`font-medium uppercase tracking-widest transition-all duration-300 ${scrolled ? 'text-sm' : 'text-base'}`}
              style={{ 
                fontFamily: fonts.heading,
                color: site.dark_mode ? '#ffffff' : '#0a0a0a'
              }}
            >
              {site.name}
            </span>
          )}
        </a>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navSections.map((section, idx) => (
            <button
              key={section.section_id || idx}
              onClick={() => scrollToSection(section.type)}
              className="text-xs uppercase tracking-widest hover:opacity-70 transition-opacity"
              style={{ 
                color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              }}
            >
              {section.title || section.type}
            </button>
          ))}
        </nav>
        
        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ color: site.dark_mode ? '#ffffff' : '#0a0a0a' }}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden absolute top-full left-0 right-0 py-4 px-6"
          style={{ 
            backgroundColor: site.dark_mode ? 'rgba(10,10,10,0.98)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <nav className="flex flex-col gap-4">
            {navSections.map((section, idx) => (
              <button
                key={section.section_id || idx}
                onClick={() => scrollToSection(section.type)}
                className="text-sm uppercase tracking-widest text-left py-2 hover:opacity-70 transition-opacity"
                style={{ 
                  color: site.dark_mode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
                }}
              >
                {section.title || section.type}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Site Footer
const SiteFooter = ({ site, sections }) => {
  const fonts = FONT_FAMILIES[site.font_package] || FONT_FAMILIES.editorial;
  const navSections = sections.filter(s => s.enabled && ['about', 'projects', 'press', 'contact', 'gallery', 'book'].includes(s.type));
  
  const scrollToSection = (sectionType) => {
    const element = document.getElementById(sectionType);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <footer 
      className="py-12 px-6"
      style={{ 
        backgroundColor: site.dark_mode ? '#050505' : '#f5f5f5',
        borderTop: `1px solid ${site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Logo / Site Name */}
          <div className="flex items-center gap-4">
            {site.logo ? (
              <img 
                src={convertGoogleDriveUrl(site.logo)} 
                alt={site.name}
                className="h-10"
              />
            ) : (
              <span 
                className="text-lg font-medium uppercase tracking-widest"
                style={{ 
                  fontFamily: fonts.heading,
                  color: site.dark_mode ? '#ffffff' : '#0a0a0a'
                }}
              >
                {site.name}
              </span>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex flex-wrap justify-center gap-6">
            {navSections.map((section, idx) => (
              <button
                key={section.section_id || idx}
                onClick={() => scrollToSection(section.type)}
                className="text-xs uppercase tracking-widest hover:opacity-70 transition-opacity"
                style={{ 
                  color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                }}
              >
                {section.title || section.type}
              </button>
            ))}
          </nav>
          
          {/* Social Links */}
          {site.social_links && Object.keys(site.social_links).some(k => site.social_links[k]) && (
            <div className="flex gap-4">
              {site.social_links.instagram && (
                <a href={site.social_links.instagram} target="_blank" rel="noopener noreferrer"
                   className="hover:opacity-70 transition-opacity"
                   style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                  <Instagram size={20} />
                </a>
              )}
              {site.social_links.twitter && (
                <a href={site.social_links.twitter} target="_blank" rel="noopener noreferrer"
                   className="hover:opacity-70 transition-opacity"
                   style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                  <Twitter size={20} />
                </a>
              )}
              {site.social_links.youtube && (
                <a href={site.social_links.youtube} target="_blank" rel="noopener noreferrer"
                   className="hover:opacity-70 transition-opacity"
                   style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                  <Youtube size={20} />
                </a>
              )}
              {site.social_links.linkedin && (
                <a href={site.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                   className="hover:opacity-70 transition-opacity"
                   style={{ color: site.dark_mode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                  <Linkedin size={20} />
                </a>
              )}
            </div>
          )}
        </div>
        
        {/* Copyright */}
        <div 
          className="mt-8 pt-8 text-center text-xs"
          style={{ 
            borderTop: `1px solid ${site.dark_mode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            color: site.dark_mode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
          }}
        >
          © {new Date().getFullYear()} {site.name}. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Error Boundary Component for catching render errors
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Section render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Return empty div instead of crashing the whole page
      return null;
    }
    return this.props.children;
  }
}

// Section renderer with error handling
const renderSection = (section, site, content) => {
  if (!section || !section.enabled) return null;
  
  // Ensure section has required properties
  const safeSection = {
    ...section,
    section_id: section.section_id || `sec_${Math.random().toString(36).substr(2, 9)}`,
    gallery_images: Array.isArray(section.gallery_images) ? section.gallery_images : [],
    items: Array.isArray(section.items) ? section.items : [],
    linked_artists: Array.isArray(section.linked_artists) ? section.linked_artists : [],
    linked_projects: Array.isArray(section.linked_projects) ? section.linked_projects : [],
    linked_companies: Array.isArray(section.linked_companies) ? section.linked_companies : [],
  };
  
  const props = { section: safeSection, site, content };
  
  const renderComponent = () => {
    switch (section.type) {
      case 'hero': return <HeroSection key={safeSection.section_id} {...props} />;
      case 'about': return <AboutSection key={safeSection.section_id} {...props} />;
      case 'projects': return <ProjectsSection key={safeSection.section_id} {...props} />;
      case 'press': return <PressSection key={safeSection.section_id} {...props} />;
      case 'video': return <VideoSection key={safeSection.section_id} {...props} />;
      case 'book': return <BookSection key={safeSection.section_id} {...props} />;
      case 'gallery': return <GallerySection key={safeSection.section_id} {...props} />;
      case 'fullwidth_image': return <FullWidthImageSection key={safeSection.section_id} {...props} />;
      case 'contact': return <ContactSection key={safeSection.section_id} {...props} />;
      case 'custom': return <CustomSection key={safeSection.section_id} {...props} />;
      default: return <CustomSection key={safeSection.section_id} {...props} />;
    }
  };
  
  return (
    <SectionErrorBoundary key={safeSection.section_id}>
      {renderComponent()}
    </SectionErrorBoundary>
  );
};

// ==================== MAIN DYNAMIC BRAND SITE PAGE ====================

export const DynamicBrandSitePage = () => {
  // Extract slug from pathname (e.g., /selina -> selina, /bethetrain -> bethetrain)
  const pathname = window.location.pathname;
  const slug = pathname.split('/')[1]; // Get first path segment
  
  const [site, setSite] = useState(null);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      
      try {
        // Fetch site config
        const siteRes = await fetch(`${API_URL}/api/brand-sites/${slug}`);
        if (!siteRes.ok) {
          setNotFound(true);
          return;
        }
        const siteData = await siteRes.json();
        setSite(siteData);

        // Fetch all content - Press section will filter by linked entities
        const contentRes = await fetch(`${API_URL}/api/content?limit=100`);
        if (contentRes.ok) {
          setContent(await contentRes.json());
        }
      } catch (error) {
        console.error('Error fetching brand site:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h1 className="text-4xl mb-4">Page Not Found</h1>
          <Link to="/" className="text-blue-400 hover:underline">Return to Cultural Revolution</Link>
        </div>
      </div>
    );
  }

  // Sort sections by order
  const sortedSections = [...(site.sections || [])].sort((a, b) => a.order - b.order);

  return (
    <div style={{ 
      backgroundColor: site.dark_mode ? '#0a0a0a' : '#ffffff',
      minHeight: '100vh'
    }}>
      <Helmet>
        <title>{site.name} | Cultural Revolution</title>
        <meta name="description" content={site.description || site.tagline} />
        <meta property="og:title" content={site.name} />
        <meta property="og:description" content={site.description || site.tagline} />
        {site.og_image && <meta property="og:image" content={site.og_image} />}
        <meta property="og:url" content={`${window.location.origin}/${site.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <style>{FONT_CSS[site.font_package] || ''}</style>
      </Helmet>

      {/* Sticky Header */}
      <SiteHeader site={site} sections={sortedSections} />

      {/* Main Content */}
      <main>
        {sortedSections.map(section => renderSection(section, site, content))}
      </main>
      
      {/* Footer */}
      <SiteFooter site={site} sections={sortedSections} />
      
      {/* CR Ecosystem Banner */}
      <CREcosystemBanner site={site} />
    </div>
  );
};

// Legacy exports for backward compatibility
export const SelinaSitePage = DynamicBrandSitePage;
export const BeTheTrainSitePage = DynamicBrandSitePage;

export default DynamicBrandSitePage;
